import React, { useMemo } from "react";
import { Box, Paper, Typography, makeStyles } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import clsx from "clsx";

import I18nText from "../../elements/I18nText";
import { MAP_DEVICE_STATUSES, getMapStatus } from "../../domain/device";

const STATUS_LABEL_KEYS = {
  ok: "devices.common.statuses.ok",
  alarm: "devices.common.statuses.alarm",
  warning: "devices.common.statuses.warning",
  error: "devices.common.statuses.error",
  threshold: "devices.list.table.statuses.threshold",
  maintenance: "devices.common.statuses.maintenance"
};

const useStyles = makeStyles(theme => ({
  root: {
    position: "absolute",
    left: theme.spacing(2),
    bottom: theme.spacing(2),
    zIndex: 1000,
    padding: theme.spacing(1, 1.5),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
    minWidth: 170,
    maxWidth: 320
  },
  title: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5)
  },
  rows: {
    display: "flex",
    flexDirection: "column"
  },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(0.25, 0.5),
    borderRadius: 4,
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover
    }
  },
  rowSelected: {
    backgroundColor: theme.palette.action.selected
  },
  rowUnselected: {
    opacity: 0.5
  },
  rowLeft: {
    display: "flex",
    alignItems: "center",
    minWidth: 0
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    marginRight: theme.spacing(1),
    flexShrink: 0
  },
  label: {
    fontSize: 13,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  count: {
    fontSize: 13,
    fontWeight: 600,
    flexShrink: 0
  },
  strikethrough: {
    textDecoration: "line-through"
  }
}));

const MapLegend = ({ devices, selectedStatuses, onToggleStatus, onReset }) => {
  const classes = useStyles();
  const theme = useTheme();

  const counts = useMemo(() => {
    const result = {};
    MAP_DEVICE_STATUSES.forEach(status => {
      result[status] = 0;
    });

    devices.forEach(device => {
      const status = getMapStatus(device);
      result[status] = (result[status] || 0) + 1;
    });

    return result;
  }, [devices]);

  const allSelected = selectedStatuses.length === MAP_DEVICE_STATUSES.length;

  return (
    <Paper className={classes.root} elevation={3}>
      <Typography variant="caption" className={classes.title}>
        <I18nText>devices.list.map.legend.title</I18nText>
      </Typography>
      <Box className={classes.rows}>
        <Box
          className={clsx(classes.row, allSelected && classes.rowSelected)}
          onClick={onReset}
        >
          <Typography className={classes.label}>
            <I18nText>topbar.filter.all</I18nText>
          </Typography>
          <Typography className={classes.count}>{devices.length}</Typography>
        </Box>
        {MAP_DEVICE_STATUSES.map(status => {
          const isSelected = selectedStatuses.includes(status);

          return (
            <Box
              key={status}
              className={clsx(
                classes.row,
                !isSelected && classes.rowUnselected
              )}
              onClick={() => onToggleStatus(status)}
            >
              <Box className={classes.rowLeft}>
                <span
                  className={classes.dot}
                  style={{
                    backgroundColor: theme.custom.colors.deviceStatus[status]
                  }}
                />
                <Typography
                  className={clsx(
                    classes.label,
                    !isSelected && classes.strikethrough
                  )}
                >
                  <I18nText>{STATUS_LABEL_KEYS[status]}</I18nText>
                </Typography>
              </Box>
              <Typography
                className={clsx(
                  classes.count,
                  !isSelected && classes.strikethrough
                )}
              >
                {counts[status]}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

export default React.memo(MapLegend);
